import api from './api';
import {
  InitPaymentResponse,
  SubscriptionPlan,
  ExpertSubscriptionStatus,
  TransactionsResponse,
  CancelSubscriptionResponse,
  Transaction,
  PlansResponse,
  PaymentOptionsResponse,
} from '../types/payment';
import { unwrapApiResponse, ApiResponse } from '@/utils/api-response.util';
import { PAYMENT_CONFIG } from '../config';

// Expert Payment API (expert user context) — independent from payment.service.ts (client procedure).
// Kept as a separate file on purpose: the client and expert checkout flows are expected to diverge.

export const getExpertPlans = async (): Promise<PlansResponse> => {
  // We need custom handling here because 'links' is outside 'data'
  const response = await api.get<ApiResponse<SubscriptionPlan[]> & { links?: { publicOffer?: string } }>('/expert-payments/plans');

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to fetch expert plans');
  }

  return {
    plans: response.data.data,
    links: response.data.links,
  };
};

export const getExpertSubscriptionStatus = async (): Promise<ExpertSubscriptionStatus> => {
  const response = await api.get<ApiResponse<ExpertSubscriptionStatus>>('/expert-payments/my-subscription');
  return unwrapApiResponse(response);
};

export const initExpertPayment = async (planId: number, billingCycle: 'month' | 'year' = 'month'): Promise<InitPaymentResponse> => {
  const headers: Record<string, string> = {};
  if (PAYMENT_CONFIG.enableSandbox) {
    headers['X-Payment-Sandbox'] = 'true';
  }

  const response = await api.post<ApiResponse<InitPaymentResponse>>(
    '/expert-payments/init',
    { planId, billingCycle },
    { headers },
  );
  return unwrapApiResponse(response);
};

// Send payment callback manually (fallback for local dev or unreliable webhooks)
export const sendExpertPaymentCallback = async (paymentData: any): Promise<any> => {
  const response = await api.post<ApiResponse<any>>('/expert-payments/callback', paymentData);
  return unwrapApiResponse(response);
};

export const cancelExpertSubscription = async (): Promise<CancelSubscriptionResponse> => {
  const response = await api.post<ApiResponse<CancelSubscriptionResponse>>('/expert-payments/cancel');
  return unwrapApiResponse(response);
};

export const getExpertTransactions = async (limit = 20, offset = 0, status?: string, type?: string): Promise<TransactionsResponse> => {
  const params: any = { limit, offset };
  if (status) params.status = status;
  if (type) params.type = type;

  const response = await api.get<ApiResponse<Transaction[]>>('/expert-payments/my-transactions', {
    params,
  });

  const { data, meta } = response.data;

  return {
    items: data,
    total: meta?.total || 0,
    limit: meta?.limit || limit,
    offset: meta?.offset || offset,
  };
};

export const getExpertPaymentOptions = async (): Promise<PaymentOptionsResponse> => {
  const response = await api.get<ApiResponse<PaymentOptionsResponse>>('/expert-payments/options');
  return unwrapApiResponse(response);
};

export const expertPaymentService = {
  getPlans: getExpertPlans,
  getMySubscription: getExpertSubscriptionStatus,
  initPayment: initExpertPayment,
  sendPaymentCallback: sendExpertPaymentCallback,
  cancelSubscription: cancelExpertSubscription,
  getMyTransactions: getExpertTransactions,
  getPaymentOptions: getExpertPaymentOptions,
};
